"use client";

import { FC, HTMLAttributes, ReactNode, useCallback, useEffect, useState } from "react";
import clsx from "clsx";

import IndustryCard, {
  IIndustryCardContent,
} from "@/components/cards/IndustryCard";
import SectionHeader from "@/sections/common/SectionHeader";

// ----------------------------------------------------------------------------
// Industries showcase — horizontal accordion of expanding industry cards.
//
// Behavior (the bit that makes this layout distinctive):
//   - 4 cards in a row at lg+, stacked on mobile.
//   - The "active" card is wider (2x the inactive widths) and reveals its
//     description + CTA; inactive cards collapse to icon + name only.
//   - Auto-rotates which card is active every 5 seconds.
//   - Hovering the active card pauses the rotation so you can read.
//   - Clicking any card promotes it to active.
//
// Polish layered on top:
//   - Canonical SectionHeader for the eyebrow/title/subtitle (matches
//     SolutionSection / ServicesSection on the same page).
//   - Section padding + typography on the canonical clamp scale.
//   - Subtle accent tint on the active card so the section's color
//     shifts as it rotates (continues the design language we use on
//     the CTA + Subscribe sections).
// ----------------------------------------------------------------------------

export type IIndustryCardSectionContent = {
  heading?: string;
  description?: ReactNode;
  cards?: IIndustryCardContent[];
  backgroundVariant?: "primary" | "white";
};

type IIndustryCardSectionProps = HTMLAttributes<HTMLElement> &
  IIndustryCardSectionContent;

// Per-position accent tint applied to the ACTIVE card. Inactive cards
// always render with the soft primary-50 base so the page reads as a
// rhythm of tints rather than four loud blocks.
const ACTIVE_ACCENTS = [
  "bg-primary-50 ring-primary-200",
  "bg-yellow-50 ring-yellow-200",
  "bg-secondary-50 ring-secondary-200",
  "bg-orange-50 ring-orange-200",
] as const;

const ROTATE_INTERVAL_MS = 5000;

const IndustryCardSection: FC<IIndustryCardSectionProps> = ({
  cards,
  heading,
  description,
  backgroundVariant = "white",
  className,
  ...other
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isCardHover, setIsCardHover] = useState<boolean>(false);

  useEffect(() => {
    if (!cards || cards.length === 0) return;
    // Respect reduced-motion preference — no auto-rotate if user opts out.
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    if (isCardHover) return;
    const timeout = window.setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % cards.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [activeIndex, isCardHover, cards]);

  const isActiveIndex = useCallback(
    (currentIndex: number) => activeIndex === currentIndex,
    [activeIndex],
  );

  return (
    <section
      className={clsx(
        backgroundVariant === "white" ? "bg-white" : "bg-primary-100",
        "py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <div className="container">
        {(heading || description) && (
          <SectionHeader
            title={heading}
            subtitle={description}
            align="center"
            tone="light"
            className="mb-10 md:mb-12 lg:mb-14"
          />
        )}

        {/* Responsive layout:
              - mobile (default): vertical stack, every card shows full content
              - md (tablet, 768+): 2-col grid, every card shows full content
              - lg+ (desktop): horizontal accordion — active card flex-[2],
                inactive flex-1, expand-on-click + auto-rotate.
            Flex-grow ratios replace the old inline `minWidth: calc(...)` so
            cards don't get forced to weird widths at small breakpoints. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:flex-row lg:gap-5 xl:gap-6">
          {cards?.length &&
            cards.map((card, index) => {
              const isActive = isActiveIndex(index);
              const accent = ACTIVE_ACCENTS[index % ACTIVE_ACCENTS.length];
              return (
                <IndustryCard
                  key={card.icon ?? index}
                  icon={card.icon}
                  heading={card.heading}
                  description={card.description}
                  button={card.button}
                  isActive={isActive}
                  openCard={() => setActiveIndex(index)}
                  className={clsx(
                    "rounded-2xl ring-1 transition-all duration-500 ease-out",
                    // Width allocation on lg+ only — accordion behavior.
                    // Below lg the grid handles sizing.
                    isActive ? "lg:flex-[2]" : "lg:flex-1",
                    isActive
                      ? `${accent} shadow-md`
                      : "bg-white ring-black-100 hover:bg-primary-50/40",
                  )}
                  onMouseEnter={() => isActive && setIsCardHover(true)}
                  onMouseLeave={() => isActive && setIsCardHover(false)}
                />
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default IndustryCardSection;
